import React, { useState, useEffect } from 'react';
import { salesAPI, productsAPI } from '../services/api';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      const [salesReport, lowStockProducts] = await Promise.all([
        salesAPI.getReport({ start_date: today, end_date: today }),
        productsAPI.getLowStock(),
      ]);

      setStats(salesReport.data);
      setLowStock(lowStockProducts.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">กำลังโหลด...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">แดชบอร์ด</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-500 text-sm">ยอดขายวันนี้ (ก่อนหักส่วนลด)</div>
          <div className="text-3xl font-bold text-primary-600 mt-2">
            ฿{(stats?.summary?.total_subtotal || 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {stats?.summary?.total_transactions || 0} รายการ
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-500 text-sm">ยอดเงินสุทธิ (หลังหักส่วนลด)</div>
          <div className="text-3xl font-bold text-green-600 mt-2">
            ฿{(stats?.summary?.total_revenue || 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {stats?.summary?.total_transactions || 0} รายการ
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-500 text-sm">ส่วนลดรวม</div>
          <div className="text-3xl font-bold text-orange-600 mt-2">
            ฿{(stats?.summary?.total_discount || 0).toFixed(2)}
          </div>
          <div className="mt-2 pt-2 border-t">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>สมาชิก:</span>
              <span className="font-semibold">฿{(stats?.summary?.member_discount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>ลูกค้าทั่วไป:</span>
              <span className="font-semibold">฿{(stats?.summary?.non_member_discount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-600">
              <span>ส่วนลดคะแนน:</span>
              <span className="font-semibold text-purple-600">฿{(stats?.summary?.points_discount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-500 text-sm">สินค้าใกล้หมด</div>
          <div className="text-3xl font-bold text-red-600 mt-2">
            {lowStock.length}
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4">วิธีชำระเงิน</h3>
          <div className="space-y-2">
            {stats?.payment_methods?.map((method) => (
              <div key={method.payment_method} className="flex justify-between items-center">
                <span className="capitalize">
                  {method.payment_method === 'cash' ? 'เงินสด' :
                   method.payment_method === 'transfer' ? 'โอนเงิน' :
                   method.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' :
                   method.payment_method}
                </span>
                <div className="text-right">
                  <div className="font-bold">฿{(method.total || 0).toFixed(2)}</div>
                  <div className="text-sm text-gray-600">{method.count || 0} รายการ</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4">สินค้าขายดี</h3>
          <div className="space-y-2">
            {stats?.top_products?.slice(0, 5).map((product) => (
              <div key={product.product_name} className="flex justify-between items-center">
                <span>{product.product_name}</span>
                <div className="text-right">
                  <div className="font-bold">{product.total_quantity || 0} ชิ้น</div>
                  <div className="text-sm text-gray-600">฿{(product.total_revenue || 0).toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-red-800 mb-4">แจ้งเตือนสินค้าใกล้หมด</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lowStock.map((product) => (
              <div key={product.id} className="bg-white p-4 rounded border border-red-300">
                <div className="font-semibold">{product.name}</div>
                <div className="text-sm text-gray-600">
                  คงเหลือ: <span className="font-bold text-red-600">{product.stock_quantity}</span>
                  {' '} / ขั้นต่ำ: {product.min_stock_level}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
